// UI Component: Keyboard Shortcuts Dialog
export class KeyboardShortcutsDialog {
  private dialogElement: HTMLElement | null = null;
  private overlayElement: HTMLElement | null = null;

  constructor() {
    this.createDialog();
  }

  private createDialog(): void {
    // Create overlay
    this.overlayElement = document.createElement('div');
    this.overlayElement.className = 'dialog-overlay';
    this.overlayElement.addEventListener('click', () => this.hide());

    // Create dialog
    this.dialogElement = document.createElement('div');
    this.dialogElement.className = 'keyboard-shortcuts-dialog';
    this.dialogElement.innerHTML = `
      <div class="dialog-header">
        <h2>⌨️ Keyboard Shortcuts</h2>
        <button class="dialog-close" title="Close">&times;</button>
      </div>
      <div class="dialog-content">
        <div class="shortcuts-section">
          <h3>File Operations</h3>
          <div class="shortcut-row">
            <span class="shortcut-keys"><kbd>Ctrl</kbd> + <kbd>N</kbd></span>
            <span class="shortcut-description">New file</span>
          </div>
          <div class="shortcut-row">
            <span class="shortcut-keys"><kbd>Ctrl</kbd> + <kbd>O</kbd></span>
            <span class="shortcut-description">Open file</span>
          </div>
          <div class="shortcut-row">
            <span class="shortcut-keys"><kbd>Ctrl</kbd> + <kbd>S</kbd></span>
            <span class="shortcut-description">Save file</span>
          </div>
          <div class="shortcut-row">
            <span class="shortcut-keys"><kbd>Ctrl</kbd> + <kbd>Shift</kbd> + <kbd>S</kbd></span>
            <span class="shortcut-description">Save As</span>
          </div>
        </div>

        <div class="shortcuts-section">
          <h3>Tab Management</h3>
          <div class="shortcut-row">
            <span class="shortcut-keys"><kbd>Ctrl</kbd> + <kbd>W</kbd></span>
            <span class="shortcut-description">Close current tab</span>
          </div>
          <div class="shortcut-row">
            <span class="shortcut-keys"><kbd>Ctrl</kbd> + <kbd>Tab</kbd></span>
            <span class="shortcut-description">Switch to next tab</span>
          </div>
          <div class="shortcut-row">
            <span class="shortcut-keys"><kbd>Ctrl</kbd> + <kbd>Shift</kbd> + <kbd>Tab</kbd></span>
            <span class="shortcut-description">Switch to previous tab</span>
          </div>
        </div>

        <div class="shortcuts-section">
          <h3>Editor</h3>
          <div class="shortcut-row">
            <span class="shortcut-keys"><kbd>Ctrl</kbd> + <kbd>F</kbd></span>
            <span class="shortcut-description">Find in file</span>
          </div>
          <div class="shortcut-row">
            <span class="shortcut-keys"><kbd>Ctrl</kbd> + <kbd>H</kbd></span>
            <span class="shortcut-description">Replace in file</span>
          </div>
          <div class="shortcut-row">
            <span class="shortcut-keys"><kbd>Ctrl</kbd> + <kbd>G</kbd></span>
            <span class="shortcut-description">Go to line</span>
          </div>
          <div class="shortcut-row">
            <span class="shortcut-keys"><kbd>Ctrl</kbd> + <kbd>/</kbd></span>
            <span class="shortcut-description">Toggle comment</span>
          </div>
        </div>

        <div class="shortcuts-section">
          <h3>Other</h3>
          <div class="shortcut-row">
            <span class="shortcut-keys"><kbd>F1</kbd> or <kbd>?</kbd></span>
            <span class="shortcut-description">Show this help</span>
          </div>
        </div>

        <p class="shortcuts-note">
          <strong>Note:</strong> On macOS, use <kbd>Cmd</kbd> instead of <kbd>Ctrl</kbd>
        </p>
      </div>
    `;

    // Add close button handler
    const closeButton = this.dialogElement.querySelector('.dialog-close');
    closeButton?.addEventListener('click', () => this.hide());

    // Prevent clicks inside dialog from closing it
    this.dialogElement.addEventListener('click', (e) => e.stopPropagation());

    // Append to body
    document.body.appendChild(this.overlayElement);
    document.body.appendChild(this.dialogElement);

    // Initially hidden
    this.hide();
  }

  show(): void {
    this.overlayElement?.classList.add('visible');
    this.dialogElement?.classList.add('visible');
    
    // Focus on dialog for accessibility
    this.dialogElement?.focus();

    // Close on Escape key
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        this.hide();
        document.removeEventListener('keydown', handleEscape);
      }
    };
    document.addEventListener('keydown', handleEscape);
  }

  hide(): void {
    this.overlayElement?.classList.remove('visible');
    this.dialogElement?.classList.remove('visible');
  }

  destroy(): void {
    this.overlayElement?.remove();
    this.dialogElement?.remove();
  }
}
